import React from 'react';
import { Box, Heading, Text } from '@medely/ui-kit';
import { HelpCenterLink } from '@mp/components/helpCenterLink';

export enum ArchiveReason {
  UnavailableMarket = 'unavailable_market',
  DormantMarket = 'dormant_market',
  LowIncrementalityMarket = 'low_incrementality_market',
}

type ProArchivedProps = {
  archiveReason?: ArchiveReason | string | null;
};

const DefaultView = () => (
  <>
    <Box py={2}>
      <Heading size="xs">Thank you for your interest in joining Medely!</Heading>
    </Box>
    <Box pb={2}>
      <Text size="m" textAlign="justify">
        At this time, your application has not been accepted because you do not meet our
        requirements for work experience and/or education.
      </Text>
    </Box>
    <Box pb={2}>
      <Text size="m" textAlign="justify">
        For details about the specific requirements for the position you applied for, please review
        the relevant article in our{' '}
        <HelpCenterLink
          url="https://help.medely.com/en/collections/1942707-rn-allied-position-descriptions"
          text="Help Center"
        />
        .
      </Text>
    </Box>

    <Text size="m" textAlign="justify">
      Once you feel that you meet the requirements, we encourage you to reach back out by sending in
      your updated resume to resume@medely.com.
    </Text>
  </>
);

const NoMarketView = () => (
  <>
    <Box py={2}>
      <Heading size="xs">Thank you for signing up to become a Medely Professional.</Heading>
    </Box>
    <Box pb={2}>
      <Text size="m" textAlign="justify">
        At Medely, we strive to match talented professionals like you with the right opportunities.
        We don&apos;t currently have available shifts for your position within 80 miles of your
        current location, but this can change as we are constantly expanding.
      </Text>
    </Box>
    <Text size="m" textAlign="justify">
      Rest assured, we&apos;ve saved your profile and will email you as soon as shifts become
      available to resume your sign-up process.
    </Text>
  </>
);

const LowIncrementalityView = () => (
  <>
    <Box py={2}>
      <Heading size="xs">Thank you for signing up to become a Medely Professional.</Heading>
    </Box>
    <Box pb={2}>
      <Text size="m" textAlign="justify">
        At Medely, we review profiles on a rolling basis and accept new professionals based on
        marketplace needs. Due to a high number of registrations, we are not accepting new sign-ups
        in your zip code or surrounding metro areas.
      </Text>
    </Box>
    <Text size="m" textAlign="justify">
      We are always expanding, and your profile has been saved in our system. We will notify you via
      email as soon as sign-ups reopen in your area.
    </Text>
  </>
);

export const ProArchived = ({ archiveReason }: ProArchivedProps) => {
  switch (archiveReason) {
    case ArchiveReason.DormantMarket:
    case ArchiveReason.UnavailableMarket:
      return <NoMarketView />;
    case ArchiveReason.LowIncrementalityMarket:
      return <LowIncrementalityView />;
    default:
      return <DefaultView />;
  }
};
